import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Checkbox, Field, Input, RadioGroup, Select } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasDelayPreview, type CanvasDelayPreviewBody } from '../../../../api/client.ts';
import { DurationField } from '../../../../components/DurationField.tsx';
import { ISO_WEEKDAYS } from '../../../campaigns/schedule/scheduledDelivery.ts';
import { NumberInput } from '../../../campaigns/trigger/NumberInput.tsx';
import { useTestUsers } from '../../../composer/shared/ProfileSelector.tsx';
import { DEFAULT_DELAY_TIME, DELAY_KINDS, delayIssues, readDelay, switchDelayKind, type DelayConfig, type DelayKind, type DelayOffset, type OnPast } from './delayConfig.ts';

export interface DelayStepPanelProps {
  environmentId: string;
  /** The step's `config` as stored. */
  value: unknown;
  /** Every edit, with the server's defaults filled in. */
  onChange: (next: DelayConfig) => void;
  readOnly?: boolean;
  /** The employee may ask the server when users wake up (`canvas.edit`). */
  canPreview?: boolean;
}

type Of<K extends DelayKind> = Extract<DelayConfig, { kind: K }>;

const ON_PAST: Array<{ value: OnPast; label: string }> = [
  { value: 'exit', label: 'End the Canvas for this user' },
  { value: 'advance_now', label: 'Go on to the next step right away' },
];

function LocalTime({ checked, readOnly, onChange }: { checked: boolean; readOnly: boolean; onChange: (on: boolean) => void }) {
  return (
    <div>
      <Checkbox label="In each user’s time zone" checked={checked} disabled={readOnly} onCheckedChange={onChange} />
      <p className="hint">{checked ? 'Users without a time zone follow the environment time zone.' : 'Off: the environment time zone for everyone (company time).'}</p>
    </div>
  );
}

function Choice<T extends string>({ label, value, options, readOnly, onChange }: { label: string; value: T; options: Array<{ value: T; label: string }>; readOnly: boolean; onChange: (next: T) => void }) {
  return (
    <div className="step-panel__choice">
      <p className="bb-field__label">{label}</p>
      <RadioGroup label={label} options={options.map((o) => ({ ...o, disabled: readOnly }))} value={value} onValueChange={(v) => onChange(v as T)} />
    </div>
  );
}

function UntilDateFields({ c, issues, readOnly, set }: { c: Of<'until_date'>; issues: Record<string, string>; readOnly: boolean; set: (next: Of<'until_date'>) => void }) {
  return (
    <>
      <div className="audience-filter">
        <Field label="Date" error={issues.date}>
          <Input type="date" value={c.date} disabled={readOnly} onChange={(e) => set({ ...c, date: e.target.value })} />
        </Field>
        <Field label="Time" error={issues.time}>
          <Input type="time" value={c.time} disabled={readOnly} onChange={(e) => set({ ...c, time: e.target.value })} />
        </Field>
      </div>
      <LocalTime checked={c.localTime} readOnly={readOnly} onChange={(localTime) => set({ ...c, localTime })} />
      <Choice label="If the date has passed when a user arrives" value={c.onPast} options={ON_PAST} readOnly={readOnly} onChange={(onPast) => set({ ...c, onPast })} />
    </>
  );
}

function DayOfWeekFields({ c, issues, readOnly, set }: { c: Of<'day_of_week'>; issues: Record<string, string>; readOnly: boolean; set: (next: Of<'day_of_week'>) => void }) {
  const toggle = (day: number, on: boolean) => set({ ...c, weekdays: on ? [...c.weekdays, day].sort((a, b) => a - b) : c.weekdays.filter((d) => d !== day) });
  return (
    <>
      <fieldset className="step-panel__days">
        <legend>Days</legend>
        {ISO_WEEKDAYS.map((d) => (
          <Checkbox key={d.day} label={d.label} checked={c.weekdays.includes(d.day)} disabled={readOnly} onCheckedChange={(on) => toggle(d.day, on)} />
        ))}
        {issues.weekdays && (
          <p className="bb-field__error" role="alert">
            {issues.weekdays}
          </p>
        )}
      </fieldset>
      <Field label="Time" error={issues.time} hint="Users who arrive on a chosen day before this time wake up that day.">
        <Input type="time" value={c.time} disabled={readOnly} onChange={(e) => set({ ...c, time: e.target.value })} />
      </Field>
      <LocalTime checked={c.localTime} readOnly={readOnly} onChange={(localTime) => set({ ...c, localTime })} />
      <Choice
        label="A user arrives on a chosen day after the time"
        value={c.lateOnChosenDay}
        options={[
          { value: 'next_occurrence', label: 'Wait for the next chosen day' },
          { value: 'advance_now', label: 'Go on to the next step right away' },
        ]}
        readOnly={readOnly}
        onChange={(lateOnChosenDay) => set({ ...c, lateOnChosenDay })}
      />
    </>
  );
}

const OFFSET_UNITS = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
];

function PersonalizedFields({ c, issues, readOnly, set }: { c: Of<'personalized'>; issues: Record<string, string>; readOnly: boolean; set: (next: Of<'personalized'>) => void }) {
  const keyLabel = c.source.from === 'context' ? 'Context variable' : 'Custom attribute';
  const setOffset = (offset: DelayOffset | undefined) => {
    const { offset: _old, ...rest } = c;
    set(offset ? { ...rest, offset } : rest);
  };
  const setTime = (time: string | undefined) => {
    const { time: _old, ...rest } = c;
    set(time === undefined ? rest : { ...rest, time });
  };
  return (
    <>
      <div className="audience-filter">
        <Field label="Value from">
          <Select
            options={[
              { value: 'attribute', label: 'A custom attribute of the profile' },
              { value: 'context', label: 'A context variable of the entry' },
            ]}
            value={c.source.from}
            disabled={readOnly}
            onChange={(e) => set({ ...c, source: { ...c.source, from: e.target.value as 'context' | 'attribute' } })}
          />
        </Field>
        <Field label={keyLabel} error={issues.key} hint={c.source.from === 'context' ? 'Set by the Canvas entry or a Context step.' : undefined}>
          <Input value={c.source.key} maxLength={255} spellCheck={false} disabled={readOnly} onChange={(e) => set({ ...c, source: { ...c.source, key: e.target.value } })} />
        </Field>
      </div>
      <p className="hint">A date with a time and an offset (`Z`, `+02:00`) is that moment; without an offset it is a wall time in the zone below; a date alone is midnight.</p>
      <Checkbox label="Before or after the value" checked={c.offset !== undefined} disabled={readOnly} onCheckedChange={(on) => setOffset(on ? { amount: 1, unit: 'days', direction: 'before' } : undefined)} />
      {c.offset && (
        <div className="audience-filter">
          <Field label="Offset" error={issues.offset}>
            <NumberInput min={0} step={1} value={Number.isFinite(c.offset.amount) ? c.offset.amount : undefined} disabled={readOnly} onValueChange={(n) => setOffset({ ...c.offset!, amount: n ?? Number.NaN })} />
          </Field>
          <Field label="Offset unit">
            <Select options={OFFSET_UNITS} value={c.offset.unit} disabled={readOnly} onChange={(e) => setOffset({ ...c.offset!, unit: e.target.value as DelayOffset['unit'] })} />
          </Field>
          <Field label="Offset direction">
            <Select
              options={[
                { value: 'before', label: 'before the value' },
                { value: 'after', label: 'after the value' },
              ]}
              value={c.offset.direction}
              disabled={readOnly}
              onChange={(e) => setOffset({ ...c.offset!, direction: e.target.value as DelayOffset['direction'] })}
            />
          </Field>
        </div>
      )}
      <Checkbox label="At a set time of day" checked={c.time !== undefined} disabled={readOnly} onCheckedChange={(on) => setTime(on ? DEFAULT_DELAY_TIME : undefined)} />
      {c.time !== undefined && (
        <Field label="Time of day" error={issues.time} hint="Replaces the time of the value (and is the time of a date-only value).">
          <Input type="time" value={c.time} disabled={readOnly} onChange={(e) => setTime(e.target.value)} />
        </Field>
      )}
      <LocalTime checked={c.localTime} readOnly={readOnly} onChange={(localTime) => set({ ...c, localTime })} />
      <Choice
        label="If the value cannot be used"
        value={c.onInvalid}
        options={[
          { value: 'exit', label: 'End the Canvas for this user' },
          { value: 'advance', label: 'Go on to the next step right away' },
        ]}
        readOnly={readOnly}
        onChange={(onInvalid) => set({ ...c, onInvalid })}
      />
      <p className="hint">The value cannot be used when it is missing, is not a date or is more than 730 days away.</p>
      <Choice label="If the time has passed when a user arrives" value={c.onPast} options={ON_PAST} readOnly={readOnly} onChange={(onPast) => set({ ...c, onPast })} />
    </>
  );
}

/** `Sun 2026-10-18 09:00` (seconds only when there are some) in a time zone. */
export function formatInZone(iso: string, zone: string | null): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: zone ?? 'UTC', weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.weekday} ${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}${parts.second !== '00' ? `:${parts.second}` : ''}`;
}

const REASONS: Record<string, string> = {
  delay_past: 'the date and time have already passed.',
  personalization_failed: 'the value is missing, is not a date or is more than 730 days away.',
};

function outcomeText(p: CanvasDelayPreview): string {
  if (p.outcome === 'wait' && p.wakeAt) return `Waits until ${formatInZone(p.wakeAt, p.zone)} (${p.zone ? `in ${p.zone}` : 'UTC'})`;
  if (p.outcome === 'exit') return `Leaves the Canvas: ${REASONS[p.reason ?? ''] ?? `${p.reason ?? 'the delay cannot be worked out'}.`}`;
  return p.reason ? `Goes on to the next step right away: the value could not be used (${p.reason}).` : 'Goes on to the next step right away.';
}

/** When a user reaching the step now would wake up (`POST …/canvas-steps/delay/preview`), for an optional test user. */
function WakeUpPreview({ environmentId, config, valid, canPreview }: { environmentId: string; config: DelayConfig; valid: boolean; canPreview: boolean }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [profileId, setProfileId] = useState('');
  const [contextValue, setContextValue] = useState('');
  const testUsers = useTestUsers(environmentId, canPreview);
  const contextKey = config.kind === 'personalized' && config.source.from === 'context' ? config.source.key.trim() : '';
  const body: CanvasDelayPreviewBody = { config, ...(profileId ? { profileId } : {}), ...(contextKey && contextValue ? { context: { [contextKey]: contextValue } } : {}) };
  const [settled, setSettled] = useState(body);
  const bodyKey = JSON.stringify(body);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(JSON.parse(bodyKey) as CanvasDelayPreviewBody), 300);
    return () => clearTimeout(timer);
  }, [bodyKey]);
  // Asked only for settings that pass the schema (the settled ones may lag behind the form by the debounce).
  const settledValid = Object.keys(delayIssues(readDelay(settled.config))).length === 0;
  const preview = useQuery({ queryKey: ['canvas-delay-preview', environmentId, settled], queryFn: () => api.previewCanvasDelay(environmentId, settled), enabled: canPreview && valid && settledValid, retry: false });

  const error = preview.error instanceof ApiError && preview.error.code === 'profile_not_found' ? 'This test user is no longer in the environment: choose another one.' : preview.isError ? 'The server could not read these settings.' : null;
  return (
    <section className="notice step-panel__summary" aria-label="When users wake up">
      <h4>When users wake up</h4>
      {!canPreview ? (
        <p className="hint">Seeing when users wake up needs the canvas.edit permission.</p>
      ) : (
        <>
          <div className="audience-filter">
            <Field label="Test user" hint={testUsers.isError ? 'Test users could not be loaded.' : 'Their time zone and attributes are used; members of content-test internal groups.'}>
              <Select
                options={[{ value: '', label: 'No test user: the environment time zone' }, ...(testUsers.data ?? []).map((m) => ({ value: m.profileId, label: `${m.externalId ?? m.emailMasked ?? m.profileId} (${m.group})` }))]}
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
              />
            </Field>
            {contextKey && (
              <Field label={`Test value of context.${contextKey}`} hint="What the entry would carry, e.g. 2026-11-01 or 2026-11-01T10:00:00Z.">
                <Input value={contextValue} onChange={(e) => setContextValue(e.target.value)} />
              </Field>
            )}
          </div>
          {!valid ? (
            <p className="hint">Fix the settings above to see when users wake up.</p>
          ) : preview.isPending || preview.isFetching ? (
            <p aria-busy="true">Working out when users wake up…</p>
          ) : error ? (
            <p role="alert">{error}</p>
          ) : preview.data ? (
            <>
              <p>{preview.data.summary}</p>
              <p role="status">
                <strong>{outcomeText(preview.data)}</strong>
              </p>
              <p className="hint">For a user who reaches the step now.</p>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Settings of a Delay step (BB-116): how long or until when users wait — a set time (up to 730 days), a calendar date,
 * a day of the week or a time from the user’s data — with the time-zone rules and what happens to late arrivals, plus
 * the server’s answer to «when would a user reaching this step now wake up».
 */
export function DelayStepPanel({ environmentId, value, onChange, readOnly = false, canPreview = true }: DelayStepPanelProps) {
  const config = readDelay(value);
  const issues = delayIssues(config);
  const valid = Object.keys(issues).length === 0;
  return (
    <section className="step-panel" aria-label="Delay settings">
      <Choice label="How long to wait" value={config.kind} options={DELAY_KINDS} readOnly={readOnly} onChange={(kind) => onChange(switchDelayKind(config, kind))} />
      {config.kind === 'duration' && (
        <DurationField
          label="Wait"
          seconds={config.seconds}
          units={['seconds', 'minutes', 'hours', 'days']}
          error={issues.seconds}
          hint="Up to 730 days; a day is 24 hours from when the user arrives."
          disabled={readOnly}
          onChange={(seconds) => onChange({ kind: 'duration', seconds })}
        />
      )}
      {config.kind === 'until_date' && <UntilDateFields c={config} issues={issues} readOnly={readOnly} set={onChange} />}
      {config.kind === 'day_of_week' && <DayOfWeekFields c={config} issues={issues} readOnly={readOnly} set={onChange} />}
      {config.kind === 'personalized' && <PersonalizedFields c={config} issues={issues} readOnly={readOnly} set={onChange} />}
      <WakeUpPreview environmentId={environmentId} config={config} valid={valid} canPreview={canPreview} />
    </section>
  );
}
